import { el } from '@/utils/dom';
import { EmailService } from '@/services/EmailService';

export interface EmailGateModalOpts {
  /** Callback invocato quando l'email è stata salvata con successo. */
  onSuccess: (email: string) => void;
  /** Callback invocato quando l'utente chiude senza inserire (opzionale). */
  onCancel?: () => void;
  /** Testo del messaggio mostrato sopra il campo. */
  message?: string;
}

/**
 * Modale di "gate": blocca l'interazione finché l'utente non inserisce
 * un'email valida. L'email è salvata in localStorage e riutilizzata
 * nelle sessioni successive.
 */
export class EmailGateModal {
  private opts: EmailGateModalOpts;
  private root?: HTMLDivElement;

  constructor(opts: EmailGateModalOpts) {
    this.opts = opts;
  }

  open(): void {
    if (this.root) return;
    this.root = el('div', {
      className: 'modal-backdrop',
      role: 'dialog',
      aria: { modal: 'true', labelledby: 'eg-modal-title' },
    }) as HTMLDivElement;

    this.root.addEventListener('click', (e) => {
      if (e.target === this.root) this.close(/* cancelled */ false);
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close(false);
        document.removeEventListener('keydown', onKey);
      }
    };
    document.addEventListener('keydown', onKey);

    this.root.appendChild(this.renderPanel());
    document.body.appendChild(this.root);
    document.body.style.overflow = 'hidden';

    queueMicrotask(() => {
      const first = this.root?.querySelector<HTMLInputElement>('#eg-email');
      first?.focus();
    });
  }

  private renderPanel(): HTMLElement {
    const message = this.opts.message ?? 'Per accedere alle impostazioni e scaricare i dati, inserisci la tua email. I dati restano sul tuo dispositivo.';

    const form = el('form', { className: 'space-y-3' }, [
      el('label', { className: 'field-label', attrs: { for: 'eg-email' }, text: 'Email' }),
      el('input', {
        className: 'input-base',
        attrs: {
          id: 'eg-email',
          type: 'email',
          inputmode: 'email',
          placeholder: 'nome@hotel.it',
          autocomplete: 'email',
          required: 'true',
          'aria-label': 'Email',
        },
        onKeyDown: (e: KeyboardEvent) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.handleSubmit();
          }
        },
      }),
      el('p', { className: 'text-xs text-slate-500', attrs: { id: 'eg-error' }, text: '' }),
      el('div', { className: 'flex items-center justify-end gap-2 pt-2' }, [
        this.opts.onCancel
          ? el('button', {
              className: 'btn-secondary',
              attrs: { type: 'button' },
              onClick: () => this.close(false),
            }, [el('span', { text: 'Annulla' })])
          : null,
        el('button', {
          className: 'btn-primary',
          attrs: { type: 'submit' },
        }, [el('span', { text: 'Continua' })]),
      ]),
    ]) as HTMLFormElement;
    form.addEventListener('submit', (e: SubmitEvent) => {
      e.preventDefault();
      this.handleSubmit();
    });

    return el('div', { className: 'modal-panel' }, [
      // Icon + title
      el('div', { className: 'flex items-start gap-3 mb-4' }, [
        el('div', { className: 'w-10 h-10 rounded-xl bg-accent-50 text-accent-600 flex items-center justify-center flex-shrink-0' }, [
          el('span', { className: 'text-xl font-bold', text: '@' }),
        ]),
        el('div', {}, [
          el('h2', { className: 'text-lg font-semibold text-slate-900', attrs: { id: 'eg-modal-title' }, text: 'Verifica email' }),
          el('p', { className: 'text-sm text-slate-500 mt-0.5', text: message }),
        ]),
      ]),
      form,
    ]);
  }

  private handleSubmit(): void {
    const input = this.root?.querySelector<HTMLInputElement>('#eg-email');
    const error = this.root?.querySelector<HTMLParagraphElement>('#eg-error');
    const email = input?.value.trim() ?? '';

    if (!EmailService.isValid(email)) {
      if (error) {
        error.textContent = 'Inserisci un’email valida, es. nome@hotel.it';
        error.classList.add('text-rose-600');
      }
      input?.focus();
      return;
    }

    EmailService.set(email);
    this.opts.onSuccess(email);
    this.close(true);
  }

  private close(success: boolean): void {
    if (!this.root) return;
    document.body.style.overflow = '';
    this.root.remove();
    this.root = undefined;
    if (!success) this.opts.onCancel?.();
  }
}
